import { useEffect, useRef } from 'react'
import { createPiece, normalise } from '../engine/piece.js'
import { backends } from '../backends/index.js'
import { fields } from '../engine/fields/index.js'
import { substrates } from '../engine/substrates/index.js'
import { markParams } from '../engine/marks.js'
import { tween, morphPhase } from '../engine/tween.js'
import { modulate, pointerSignal } from '../engine/modulation.js'
import { resolveGround } from '../engine/color.js'

// Structural params force a rebuild; continuous and cosmetic ones are written
// straight into the live config objects the field/substrate closures captured.
// This is the cost-of-change tiering made real: dragging a frequency slider
// never reallocates a substrate.
// Read from the schemas rather than a hand-kept list of key names: a new
// generator type declares its own structural params and is picked up here
// automatically. The old allowlist silently failed to rebuild for any key it
// had not been taught about, which is the same failure mode the generated
// panel exists to avoid.
function collect(row, params, cfg) {
  for (const p of params) if (p.tier === 'structural') row.push(cfg[p.key])
}

function structuralKey(p) {
  const parts = [p.seed]
  for (const l of p.layers) {
    const field = fields[l.field.type] || fields.interference
    const substrate = substrates[l.substrate.type] || substrates.grid
    const row = [l.enabled, l.field.type, l.substrate.type]
    collect(row, field.params, l.field)
    collect(row, substrate.params, l.substrate)
    collect(row, markParams, l.mark)
    parts.push(row)
  }
  return JSON.stringify(parts)
}

function applyLive(target, source) {
  Object.assign(target.clock, source.clock)
  Object.assign(target.frame, source.frame)
  source.layers.forEach((l, i) => {
    const t = target.layers[i]
    if (!t) return
    Object.assign(t.field, l.field)
    Object.assign(t.substrate, l.substrate)
    Object.assign(t.motion, l.motion)
    Object.assign(t.mark, l.mark)
    Object.assign(t.ink, l.ink)
    Object.assign(t.transform, l.transform)
    t.timeScale = l.timeScale
    t.phaseOffset = l.phaseOffset
    t.enabled = l.enabled
  })
}

export function useEngine(canvasRef, spec, { backend = 'canvas2d', theme, morph = null } = {}) {
  const engineRef = useRef(null)
  const keyRef = useRef(null)
  const specRef = useRef(spec)
  specRef.current = spec

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const factory = backends[backend]
    if (!factory || factory.available === false) return
    const renderer = factory.create(canvas)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Pointer modulation. `hover` eases toward its target so entering and
    // leaving the canvas are both gradual — a hard 0/1 would snap whatever is
    // bound, which is the opposite of reacting.
    let hover = 0, hoverTarget = 0, pointerX = 0
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect()
      pointerX = r.width ? ((e.clientX - r.left) / r.width) * 2 - 1 : 0
      hoverTarget = 1
    }
    const onLeave = () => { hoverTarget = 0 }

    let raf = null
    let last = performance.now()
    let ground = '#F0EEE6'
    let ink = '#23221F'

    const readTheme = () => {
      const cs = getComputedStyle(document.documentElement)
      ground = cs.getPropertyValue('--art-paper').trim() || '#F0EEE6'
      ink = cs.getPropertyValue('--art-ink').trim() || '#23221F'
    }

    const resize = () => {
      const r = canvas.getBoundingClientRect()
      if (r.width && r.height) renderer.resize(r.width, r.height, window.devicePixelRatio)
    }

    // A morph is per-frame work, so it happens here rather than in React
    // state: blending through setPiece at 60fps would re-render the whole
    // panel every frame for a value the panel is not showing anyway.
    const target = morph ? normalise(morph.to) : null
    const startedAt = performance.now()

    const ensureEngine = () => {
      let p = normalise(specRef.current)
      if (target) {
        const u = morphPhase(performance.now() - startedAt, morph.ms, morph.loop)
        p = tween(p, target, u)
      }
      // After the blend, before the key: modulation only touches continuous
      // and cosmetic params, so it can never force a rebuild.
      p = modulate(p, pointerSignal(hover, wallSeconds, pointerX, p.pointer.jitter))
      const key = structuralKey(p)
      if (key !== keyRef.current) {
        keyRef.current = key
        engineRef.current = createPiece(p)
      } else if (engineRef.current) {
        applyLive(engineRef.current.piece, p)
      }
      return engineRef.current
    }

    // Wall time, not the piece clock: a hover should still wander when the
    // clock is paused, and reading it here rather than from the engine keeps
    // ensureEngine free of an ordering dependency on the rest of the frame.
    let wallSeconds = 0
    const frame = (now) => {
      wallSeconds = now / 1000
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      // Exponential ease, framerate-independent: about 180ms to settle.
      hover += (hoverTarget - hover) * Math.min(1, dt * 6)
      const engine = ensureEngine()
      const cfg = engine.piece

      const t = reduced ? 1.7 : clockAdvance(cfg.clock, dt)
      renderer.clear(resolveGround(cfg.frame, ground), cfg.layers[0] ? cfg.layers[0].ink.persistence : 0)
      for (const drawn of engine.update(t)) renderer.drawLayer(drawn, cfg.frame, ink, t)
      if (!reduced) raf = requestAnimationFrame(frame)
    }

    // Local accumulator so the clock survives live cfg swaps.
    let acc = 0
    function clockAdvance(cfg, dt) {
      acc += dt * cfg.rate
      if (cfg.loop > 0) acc %= cfg.loop
      return acc + cfg.phase
    }

    readTheme()
    resize()
    raf = requestAnimationFrame(frame)

    canvas.addEventListener('pointermove', onMove)
    canvas.addEventListener('pointerleave', onLeave)
    canvas.addEventListener('pointercancel', onLeave)

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onTheme = () => readTheme()
    mq.addEventListener('change', onTheme)
    const mo = new MutationObserver(onTheme)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })

    return () => {
      if (raf) cancelAnimationFrame(raf)
      canvas.removeEventListener('pointermove', onMove)
      canvas.removeEventListener('pointerleave', onLeave)
      canvas.removeEventListener('pointercancel', onLeave)
      ro.disconnect()
      mo.disconnect()
      mq.removeEventListener('change', onTheme)
      renderer.dispose()
    }
    // theme is a dependency so an explicit toggle repaints immediately
  }, [canvasRef, backend, theme, morph])

  return engineRef
}
